/**
 * Pure state machine for a maneuver session.
 *
 * Frame-by-frame logic for the Practice/Career flow — no Angular, no
 * signals. The Angular-aware {@link ManeuverStore} wraps this class and
 * re-publishes its internal state via signals; tests should target this
 * pure layer directly.
 *
 * See `maneuver.store.ts` for the lifecycle diagram.
 */

import type {
  BoatImpactEvent,
  AppState,
  BoatTelemetry,
} from "../../../simulation-core";
import {
  EMPTY_WINDOW,
  anchorScopeRatio,
  buildChecklistContext,
  countFenders,
  deriveAnchorState,
  distanceFromTarget,
  type ManeuverWindow,
  type TargetGeometry,
} from "./maneuver-context";
import { computeFinalScore, type ScoreBreakdown } from "./scoring.engine";
import { evaluateAutoCheck, type ChecklistContext } from "./command-checklist";
import type {
  DifficultyTierId,
  ManeuverDef,
  ScoringConfig,
} from "./gamification.types";

export type ManeuverSessionPhase =
  | "idle"
  | "declared"
  | "in_progress"
  | "stabilizing"
  | "completed"
  | "failed"
  | "aborted";

export interface ManeuverDeclaration {
  readonly def: ManeuverDef;
  readonly tier: DifficultyTierId;
  readonly boatId: string;
  readonly terrainId: string;
  readonly target: TargetGeometry | null;
  readonly isFirstAttempt: boolean;
}

export interface ManeuverResult {
  readonly phase: "completed" | "failed" | "aborted";
  readonly elapsedSec: number;
  readonly window: ManeuverWindow;
  readonly breakdown: ScoreBreakdown | null;
  /** Map of `${phaseId}.${stepId}` → first tick when the auto-check fired. */
  readonly checklist: ReadonlyMap<string, number>;
  readonly failureReason:
    | null
    | "severe_collision"
    | "time_limit"
    | "aborted_by_user";
}

export interface ManeuverSessionState {
  readonly phase: ManeuverSessionPhase;
  readonly declaration: ManeuverDeclaration | null;
  readonly startTimeMs: number | null;
  readonly elapsedSec: number;
  readonly window: ManeuverWindow;
  /** Time of the most recent successful-state evaluation (for stability window). */
  readonly stabilityEnteredMs: number | null;
  readonly checklist: ReadonlyMap<string, number>;
  readonly result: ManeuverResult | null;
}

export const IDLE_STATE: ManeuverSessionState = {
  phase: "idle",
  declaration: null,
  startTimeMs: null,
  elapsedSec: 0,
  window: EMPTY_WINDOW,
  stabilityEnteredMs: null,
  checklist: new Map(),
  result: null,
};

export interface TickInput {
  readonly nowMs: number;
  readonly dtSec: number;
  readonly state: AppState;
  readonly telemetry: BoatTelemetry;
  readonly impactEvents: readonly BoatImpactEvent[];
  readonly overTensionedCleats?: readonly string[];
  readonly scoring: ScoringConfig;
}

/** Pure state machine — no DI, no signals. */
export class ManeuverStateMachine {
  private _state: ManeuverSessionState = IDLE_STATE;

  get state(): ManeuverSessionState {
    return this._state;
  }

  /** Move from `idle` to `declared`. No-op if already declared/active. */
  declare(declaration: ManeuverDeclaration): void {
    if (this._state.phase !== "idle") return;
    this._state = {
      ...IDLE_STATE,
      phase: "declared",
      declaration,
    };
  }

  /** Move from `declared` to `in_progress`. Records start timestamp. */
  start(nowMs: number): void {
    const s = this._state;
    if (s.phase !== "declared") return;
    this._state = {
      ...s,
      phase: "in_progress",
      startTimeMs: nowMs,
      elapsedSec: 0,
      window: EMPTY_WINDOW,
      stabilityEnteredMs: null,
      checklist: new Map(),
    };
  }

  /** Sim tick — frame or fixed-cadence. Idempotent on terminal states. */
  tick(input: TickInput): void {
    const s = this._state;
    if (s.phase !== "in_progress" && s.phase !== "stabilizing") return;
    if (s.declaration === null || s.startTimeMs === null) return;

    const def = s.declaration.def;
    const elapsedSec = (input.nowMs - s.startTimeMs) / 1000;

    // --- accumulate window observations ----------------------------------
    let window = s.window;
    const dt = Math.max(0, input.dtSec);
    const anchor = deriveAnchorState(
      input.state.anchorState,
      input.state.anchorHoldingState,
    );
    if (anchor === "dragging") {
      window = {
        ...window,
        anchorDragSec: window.anchorDragSec + dt,
        secondsSinceLastDrag: 0,
        hadDrag: true,
      };
    } else {
      window = {
        ...window,
        secondsSinceLastDrag: window.secondsSinceLastDrag + dt,
      };
    }
    // Track cumulative absolute heading change. Wrap-aware: a delta of
    // ±π is the maximum per-tick magnitude we ever credit.
    const headingRad = input.telemetry.heading;
    if (window.lastHeadingRad !== null) {
      let d = headingRad - window.lastHeadingRad;
      while (d > Math.PI) d -= 2 * Math.PI;
      while (d < -Math.PI) d += 2 * Math.PI;
      window = {
        ...window,
        cumulativeTurnDeg:
          window.cumulativeTurnDeg + (Math.abs(d) * 180) / Math.PI,
        lastHeadingRad: headingRad,
      };
    } else {
      window = { ...window, lastHeadingRad: headingRad };
    }
    // Detect "shift under load": a sign flip in signed throttle while the
    // previous magnitude was above the high-RPM threshold. Real outboards
    // don't enjoy slamming F↔R at high RPM, so we model the abuse as an
    // XP penalty rather than a hard failure.
    const curThrottle = input.telemetry.throttle;
    if (window.lastThrottle !== null) {
      const prev = window.lastThrottle;
      const signFlip =
        (prev > 0 && curThrottle < 0) || (prev < 0 && curThrottle > 0);
      if (
        signFlip &&
        Math.abs(prev) >= input.scoring.penalties.gearShiftHighRpmThrottle
      ) {
        window = {
          ...window,
          highRpmGearShifts: window.highRpmGearShifts + 1,
        };
      }
    }
    window = { ...window, lastThrottle: curThrottle };
    if (input.impactEvents.length > 0) {
      let worst = window.maxContactSpeedMs;
      let collided = window.hadCollision;
      let buoyHits = window.buoyContactCount;
      // Buoy contact only counts as a penalised collision when the
      // maneuver explicitly opts in (slalom / circle). Mooring-to-buoy
      // and anchoring maneuvers expect gentle buoy nudges, so the
      // default is `false` and they pass without setting the flag.
      const buoyPenalized = def.buoyContactPenalized === true;
      for (const ev of input.impactEvents) {
        if (ev.kind === "buoy") {
          buoyHits++;
          if (buoyPenalized) {
            if (ev.speed > worst) worst = ev.speed;
            collided = true;
          }
        } else {
          if (ev.speed > worst) worst = ev.speed;
          collided = true;
        }
      }
      window = {
        ...window,
        maxContactSpeedMs: worst,
        hadCollision: collided,
        buoyContactCount: buoyHits,
      };
    }
    if (input.overTensionedCleats && input.overTensionedCleats.length > 0) {
      const merged = new Set(window.overTensionedLines);
      for (const c of input.overTensionedCleats) merged.add(c);
      window = { ...window, overTensionedLines: merged };
    }

    // --- failure short-circuits ------------------------------------------
    if (
      window.maxContactSpeedMs >= input.scoring.penalties.severeCollisionSpeedMs
    ) {
      this.finalizeFailure(
        { ...s, window, elapsedSec },
        "severe_collision",
        input.scoring,
      );
      return;
    }
    if (elapsedSec >= def.timeLimitSec) {
      this.finalizeFailure(
        { ...s, window, elapsedSec },
        "time_limit",
        input.scoring,
      );
      return;
    }

    // --- success / stabilization handling --------------------------------
    const meetsRequirements = evaluateRequirements({
      def,
      state: input.state,
      telemetry: input.telemetry,
      target: s.declaration.target,
      window,
    });

    let phase = s.phase;
    let stabilityEnteredMs = s.stabilityEnteredMs;
    const stabilityWindowSec =
      typeof def.requirements["stabilityWindowSec"] === "number"
        ? (def.requirements["stabilityWindowSec"] as number)
        : 0;
    if (meetsRequirements) {
      if (phase === "in_progress") {
        phase = "stabilizing";
        stabilityEnteredMs = input.nowMs;
      } else if (
        phase === "stabilizing" &&
        stabilityEnteredMs !== null &&
        (input.nowMs - stabilityEnteredMs) / 1000 >= stabilityWindowSec
      ) {
        this.finalizeSuccess({ ...s, window, elapsedSec }, input.scoring);
        return;
      }
    } else if (phase === "stabilizing") {
      phase = "in_progress";
      stabilityEnteredMs = null;
    }

    // --- checklist auto-check latch --------------------------------------
    const checklist = updateChecklist({
      def,
      prior: s.checklist,
      ctx: buildChecklistContext({
        state: input.state,
        telemetry: input.telemetry,
        window,
        target: s.declaration.target,
      }),
      tickSec: elapsedSec,
    });

    this._state = {
      ...s,
      phase,
      elapsedSec,
      window,
      stabilityEnteredMs,
      checklist,
    };
  }

  /** Abort from any non-terminal state. */
  abort(): void {
    const s = this._state;
    if (
      s.phase === "completed" ||
      s.phase === "failed" ||
      s.phase === "aborted" ||
      s.phase === "idle"
    ) {
      return;
    }
    this._state = {
      ...s,
      phase: "aborted",
      result: {
        phase: "aborted",
        elapsedSec: s.elapsedSec,
        window: s.window,
        breakdown: null,
        checklist: s.checklist,
        failureReason: "aborted_by_user",
      },
    };
  }

  /** Return to the initial idle state. */
  reset(): void {
    this._state = IDLE_STATE;
  }

  private finalizeFailure(
    s: ManeuverSessionState,
    reason: "severe_collision" | "time_limit",
    scoring: ScoringConfig,
  ): void {
    const breakdown = this.buildBreakdown(s, scoring);
    this._state = {
      ...s,
      phase: "failed",
      result: {
        phase: "failed",
        elapsedSec: s.elapsedSec,
        window: s.window,
        breakdown,
        checklist: s.checklist,
        failureReason: reason,
      },
    };
  }

  private finalizeSuccess(
    s: ManeuverSessionState,
    scoring: ScoringConfig,
  ): void {
    const breakdown = this.buildBreakdown(s, scoring);
    this._state = {
      ...s,
      phase: "completed",
      result: {
        phase: "completed",
        elapsedSec: s.elapsedSec,
        window: s.window,
        breakdown,
        checklist: s.checklist,
        failureReason: null,
      },
    };
  }

  private buildBreakdown(
    s: ManeuverSessionState,
    scoring: ScoringConfig,
  ): ScoreBreakdown | null {
    if (s.declaration === null) return null;
    const def = s.declaration.def;
    return computeFinalScore(
      {
        baseXp: def.baseXp,
        tier: s.declaration.tier,
        // MVP: requirements gate the outcome; performance metrics will be
        // populated in a later iteration.
        performanceMetrics: {},
        elapsedSec: s.elapsedSec,
        targetTimeSec: def.targetTimeSec,
        timeLimitSec: def.timeLimitSec,
        isFirstAttempt: s.declaration.isFirstAttempt,
        maxContactSpeedMs: s.window.maxContactSpeedMs,
        anchorDragSec: s.window.anchorDragSec,
        overTensionedLineCount: s.window.overTensionedLines.size,
        highRpmGearShiftCount: s.window.highRpmGearShifts,
      },
      scoring,
    );
  }
}

// ----- requirement & checklist helpers --------------------------------------

interface RequirementInput {
  readonly def: ManeuverDef;
  readonly state: AppState;
  readonly telemetry: BoatTelemetry;
  readonly target: TargetGeometry | null;
  readonly window: ManeuverWindow;
}

/**
 * Evaluate the maneuver's `requirements` block against the current sim
 * snapshot. Returns `true` when **all** known requirement keys pass.
 * Unknown keys are skipped — the configs are deliberately tolerant.
 */
function evaluateRequirements(input: RequirementInput): boolean {
  const r = input.def.requirements;
  const anchor = deriveAnchorState(
    input.state.anchorState,
    input.state.anchorHoldingState,
  );
  const lines = input.state.mooringTargets;

  if (typeof r["anchorState"] === "string" && anchor !== r["anchorState"]) {
    return false;
  }
  if (typeof r["anchorScopeRatioMin"] === "number") {
    const ratio = anchorScopeRatio(input.state);
    if (
      !(Number.isFinite(ratio) && ratio >= (r["anchorScopeRatioMin"] as number))
    ) {
      return false;
    }
  }
  if (typeof r["noDragForSec"] === "number") {
    if (input.window.secondsSinceLastDrag < (r["noDragForSec"] as number)) {
      return false;
    }
  }
  if (typeof r["stabilityMaxSpeedMs"] === "number") {
    if (input.telemetry.sog > (r["stabilityMaxSpeedMs"] as number))
      return false;
  }
  if (Array.isArray(r["requiredLines"])) {
    for (const entry of r["requiredLines"] as string[]) {
      const alternatives = entry.split("|");
      const ok = alternatives.some((cleat) => lines[cleat] != null);
      if (!ok) return false;
    }
  }
  if (r["allLinesStowed"] === true) {
    const anyAttached = Object.values(lines).some((v) => v !== null);
    if (anyAttached) return false;
  }
  if (typeof r["minDistanceFromTargetM"] === "number") {
    const d = distanceFromTarget(
      { x: input.telemetry.posX, z: input.telemetry.posZ },
      input.target,
    );
    if (d < (r["minDistanceFromTargetM"] as number)) return false;
  }
  if (typeof r["minDistanceFromDockM"] === "number") {
    const d = distanceFromTarget(
      { x: input.telemetry.posX, z: input.telemetry.posZ },
      input.target,
    );
    if (d < (r["minDistanceFromDockM"] as number)) return false;
  }
  if (r["fenderSideMatchesDock"] === true) {
    const f = countFenders(input.state.fenderDeployed);
    if (f.port + f.starboard === 0) return false;
  }
  if (r["noCollisions"] === true && input.window.hadCollision) return false;
  if (typeof r["turnedDegreesMin"] === "number") {
    if (input.window.cumulativeTurnDeg < (r["turnedDegreesMin"] as number))
      return false;
  }
  return true;
}

function updateChecklist(input: {
  readonly def: ManeuverDef;
  readonly prior: ReadonlyMap<string, number>;
  readonly ctx: ChecklistContext;
  readonly tickSec: number;
}): ReadonlyMap<string, number> {
  if (input.def.commandSequence.length === 0) return input.prior;
  let next: Map<string, number> | null = null;
  for (const phase of input.def.commandSequence) {
    for (const step of phase.steps) {
      const key = `${phase.id}.${step.id}`;
      if (input.prior.has(key)) continue;
      if (evaluateAutoCheck(step.autoCheck, input.ctx)) {
        if (next === null) next = new Map(input.prior);
        next.set(key, input.tickSec);
      }
    }
  }
  return next ?? input.prior;
}
